import type { FC } from "react";
import { useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { Button, EmptyState, Tab, TabPanel, Tabs } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import DownloadButtonMenu from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { translate } from "app/filters/i18nFilter";
import { ShareOfVoiceProvider } from "app/pages/digital-marketing/keywords-campaigns/reports/overview-report/Performance-overtime/others-context/OthersContext";
import { pngDownload } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { buttonTypes } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { useMonitorKeywordsEndpoints } from "../../monitorKeywordsEndpoints";
import { useMonitorKeywordsNavigationTargets } from "../../monitorKeywordsNavigationTargets";
import { ShareOfVoiceGraph } from "../share-of-voice/ShareOfVoiceGraph";
import { getNoDataObject } from "../share-of-voice/utils";
import {
    Footer,
    GraphHeaderActions,
    MetricContainerStyled,
    MonitorKeywordsGraphWrapper,
    ShareOfVoiceLoader,
    ShareOfVoiceWrapper,
    StyledTab,
    StyledTabList,
    TabsContainer,
} from "../StyledComponents";
import { VolumeOverTimeGraph } from "../volume-over-time/VolumeOverTimeGraph";

import type { IMonitorKeywordsGraphProps } from "./types";
import { EMonitorKeywordsGraphTabs } from "./types";
import { getExcelLink } from "./utils";

export const MonitorKeywordsGraph: FC<IMonitorKeywordsGraphProps> = ({
    keywordGroup,
    activationSets,
    isActivationSetError,
    isActivationSetPlaceholderData,
}) => {
    const { activationSetId, country, duration, tab } = useCurrentStateAndParams().params;
    const endpoints = useMonitorKeywordsEndpoints();
    const navTargets = useMonitorKeywordsNavigationTargets();
    const isExcelAllowed = swSettings.current.resources.IsExcelAllowed as boolean;
    const selectedActivationSetDomains = activationSets?.find((set) => set.id === activationSetId)?.allDomains;
    const tabsContainerRef = useRef<HTMLDivElement>(null);

    const handlePngDownload = () => {
        return pngDownload(
            tabsContainerRef.current,
            translate("keywordanalysis.organic2.clicks.over.time.title.tooltip"),
        );
    };

    const onTabSelect = (tabIndex) => {
        const tabName = EMonitorKeywordsGraphTabs[tabIndex];
        void proNavigator.updateParams({ tab: tabName });
    };

    const ctaLink = proNavigator.href(navTargets.serpPlayersState, {
        country,
        duration,
        keyword: `*${keywordGroup.Id}`,
    });

    const excelLink = getExcelLink(
        country,
        tab,
        keywordGroup,
        selectedActivationSetDomains,
        endpoints.shareOfVoiceExcel,
        endpoints.volumeOverTimeExcel,
    );

    const noDataObject = getNoDataObject(isActivationSetError);

    const getShareOfVoiceTab = () => {
        if (isActivationSetError) {
            return (
                <ShareOfVoiceWrapper>
                    <EmptyState {...noDataObject} />
                </ShareOfVoiceWrapper>
            );
        } else if (isActivationSetPlaceholderData || !selectedActivationSetDomains) {
            return (
                <ShareOfVoiceWrapper>
                    <ShareOfVoiceLoader />
                </ShareOfVoiceWrapper>
            );
        } else {
            return (
                <ShareOfVoiceProvider>
                    <ShareOfVoiceGraph
                        keywordGroup={keywordGroup}
                        domains={selectedActivationSetDomains}
                    />
                </ShareOfVoiceProvider>
            );
        }
    };

    return (
        <MonitorKeywordsGraphWrapper data-automation="MonitorKeywordsGraph">
            {activationSetId ? (
                <TabsContainer ref={tabsContainerRef}>
                    <Tabs
                        onSelect={onTabSelect}
                        forceRenderTabPanel={true}
                        defaultIndex={EMonitorKeywordsGraphTabs.ShareOfVoice}
                    >
                        <StyledTabList>
                            <FlexRow>
                                <StyledTab>{translate("monitor.keyword.page.graph.tab.share.of.voice")}</StyledTab>
                                <Tab>{translate("monitor.keyword.page.graph.tab.volume")}</Tab>
                            </FlexRow>
                            <GraphHeaderActions>
                                <DownloadButtonMenu
                                    Excel={true}
                                    downloadUrl={excelLink}
                                    excelLocked={!isExcelAllowed}
                                />
                                <DownloadButtonMenu
                                    PNG={true}
                                    handleDownloadFunction={handlePngDownload}
                                />
                            </GraphHeaderActions>
                        </StyledTabList>
                        <TabPanel>
                            {getShareOfVoiceTab()}
                            <Footer>
                                <a href={ctaLink}>
                                    <Button type={buttonTypes.FLAT}>
                                        {translate("monitor.keyword.page.share.of.voice.cta")}
                                    </Button>
                                </a>
                            </Footer>
                        </TabPanel>
                        <TabPanel>
                            <VolumeOverTimeGraph
                                keywordGroup={keywordGroup}
                                displayHeader={false}
                                containerComponent={MetricContainerStyled}
                            />
                        </TabPanel>
                    </Tabs>
                </TabsContainer>
            ) : (
                <VolumeOverTimeGraph
                    keywordGroup={keywordGroup}
                    displayHeader={true}
                />
            )}
        </MonitorKeywordsGraphWrapper>
    );
};
